import { DeepValue, DotSelector, RequiredKeys } from '../types';
import { Identity, Predicate } from '../interfaces/functions.interface';
import { isNil } from '@alk/core';
import get from 'lodash/get';

export function stringifyPlainProps(request: unknown): string {
    return Boolean(request) ? Object.entries(request).map(([ key, value ]) => `${ key }=${ value }`).join('|') : '';
}

export function getEnumKeyByEnumValue<T extends {[ index:string ]:string}>(
    srcEnum: T,
    enumValue: string,
    fallbackValue: keyof T | null = null,
): keyof T | null {
    const key = Object.keys(srcEnum).find(key => srcEnum[ key ] === enumValue);
    return isNil(key) ? fallbackValue : key;
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name;

export const singlePropertyMutation = <TItem, K extends keyof TItem>(prop: K, value: TItem[K]): (Identity<TItem>) =>
    (item: TItem): TItem => ({ ...item, ...{ [ prop ]: value } });

export const enumTransformer =
    <Tin, Tout>(valueName: string, enumMap: Map<Tin, Tout>, customTransformer: () => Tout|undefined = undefined) =>
        (value: Tin): Tout => {

            if (customTransformer?.() !== undefined){
                return customTransformer?.();
            }

            if (enumMap.has(value)){
                return enumMap.get(value);
            }

            throw new Error(`[enumTransformer] Provided ${ valueName } ${ value } not supported`);
        };

export const referenceEqualer = <T>(first: T, second: T): boolean => first === second;

export const referenceEqualerCurried = <T>(first: T) => (second: T): boolean => referenceEqualer(first, second);

export const non = <T extends ((...args: Array<unknown>) => boolean)>(fun: T) => (...params: Parameters<T>): boolean => !fun(...params);

export const pickFilter = <T, K extends keyof T>(key: K, predicate: Predicate<T[K]> = undefined) =>
    (value: T): boolean => predicate?.(value[ key ]);

export const pickCurried =
    <T, P extends keyof T>(prop: P) =>
        (item: T): T[P] => item?.[ prop ];

export const isUnique = <T>(current: T, idx: number, items: Array<T>): boolean =>
    items.indexOf(current) === idx;

export const typedGet = <T, P extends DotSelector<T> & string>
(property: P, fallBack: DeepValue<T, P> = undefined) => (subj: T): DeepValue<T, P> =>
    get(subj, property, fallBack);

export const ensurerObjectType = <T>(obj: unknown, propsArray: ReadonlyArray<RequiredKeys<T>>): obj is T =>
    propsArray.every(prop => prop as string in (obj as object));

export const mergeToObject = <T>(accumulator: T, increment: Partial<T>): T => ({ ...accumulator, ...increment });
